import React, { useState, useEffect, useRef } from "react";
import "./RecWiseCode.css";
import { useNavigate } from 'react-router-dom';
import CTA from './CTA';

const ReportsInsightsModule = () => {
  const [activeSection, setActiveSection] = useState("real-time-dashboards");
  const [visibleSections, setVisibleSections] = useState(new Set());
  const sectionsRef = useRef({});
  const navigate = useNavigate();
  const handleReports = () => navigate('/hranalytics');
    


  const sectionContent = {
    "real-time-dashboards": {
      title: "Real-Time Dashboards",
      description: "Monitor key HR metrics and organizational performance with interactive, real-time dashboards. Track headcount trends, turnover rates, diversity metrics, recruitment efficiency, and workforce costs in visually compelling formats. Configure personalized dashboards for different leadership levels with role-specific KPIs and drill-down capabilities. Enable data refresh in real-time, schedule automated updates, and share insights across the organization for data-driven decision making at all levels."
    },
    "custom-reporting": {
      title: "Customizable Reporting",
      description: "Create tailored reports for any HR need with our flexible reporting engine. Generate standard reports for compliance, operational metrics, and strategic analysis or build custom reports from scratch. Use drag-and-drop interfaces to select data fields, apply filters, and design report layouts. Schedule automated report generation and distribution to stakeholders. Export reports in multiple formats including PDF, Excel, and PowerPoint for presentation and further analysis."
    },
    "predictive-analytics": {
      title: "Predictive Workforce Analytics",
      description: "Transform historical data into future insights with advanced predictive analytics capabilities. Forecast turnover risks, identify retention patterns, predict hiring needs, and anticipate skill gaps before they impact the business. Use machine learning algorithms to analyze workforce trends and provide early warning indicators for critical HR issues. Generate predictive models for workforce planning, talent acquisition, and succession management based on historical patterns and external market data."
    },
    "compliance-reporting": {
      title: "Automated Compliance",
      description: "Ensure regulatory compliance with automated reporting for all statutory requirements. Generate EEO-1 reports, OSHA logs, pay equity analyses, and other compliance documentation with built-in validation and accuracy checks. Schedule compliance reports for automatic submission deadlines and maintain audit trails for all compliance activities. Track compliance status across different jurisdictions and provide alerts for upcoming reporting requirements and regulatory changes."
    },
    "data-visualization": {
      title: "Advanced Data Visualization",
      description: "Communicate complex HR data effectively with sophisticated visualization tools. Create interactive charts, graphs, heat maps, and organizational charts that make data insights accessible and actionable. Implement drill-down capabilities, hover details, and filtering options for deeper data exploration. Customize visualization styles, color schemes, and branding to match organizational standards. Enable storytelling with data through guided analytics and narrative-driven insights presentations."
    }
  };

  const sections = [
    { id: "real-time-dashboards", label: "DASHBOARDS" },
    { id: "custom-reporting", label: "REPORTING" },
    { id: "predictive-analytics", label: "PREDICTIVE" },
    { id: "compliance-reporting", label: "COMPLIANCE" },
    { id: "data-visualization", label: "VISUALIZATION" }
  ];

  useEffect(() => {
    const onScroll = () => {
      const pos = window.scrollY + 150;
      for (const sec of sections) {
        const el = sectionsRef.current[sec.id];
        if (!el) continue;
        if (pos >= el.offsetTop && pos < el.offsetTop + el.offsetHeight) {
          setActiveSection(sec.id);
          break;
        }
      }
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, en.target.dataset.section]));
          }
        });
      },
      { threshold: 0.15 }
    );
    Object.values(sectionsRef.current).forEach((el) => el && obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const scrollToSection = (id) => {
    const el = sectionsRef.current[id];
    if (el) window.scrollTo({ top: el.offsetTop - 100, behavior: "smooth" });
  };

  return (
    <div className="resto-main-wrapper">
      {/* <nav className="resto-sticky-navbar">
        <div className="resto-nav-container">
          {sections.map((s) => (
            <button
              key={s.id}
              className={`resto-nav-button ${activeSection === s.id ? "resto-active" : ""}`}
              onClick={() => scrollToSection(s.id)}
            >
              {s.label}
            </button>
          ))}
        </div>
      </nav> */}

      <div className="resto-content-wrapper">

        {/* 1. Real-Time Dashboards */}
        <section
          ref={(el) => (sectionsRef.current["real-time-dashboards"] = el)}
          data-section="real-time-dashboards"
          className={`resto-section ${visibleSections.has("real-time-dashboards") ? "resto-visible" : ""}`}
        >
          <div className="resto-section-content resto-fade-in-left">
            <div className="resto-section-label">Dashboards</div>
            <h2 className="resto-section-title">
              {sectionContent["real-time-dashboards"].title}
            </h2>
            <p className="resto-section-description">
              {sectionContent["real-time-dashboards"].description}
            </p>

            <div className="resto-buttons-group">
              <button className="resto-primary-btn" onClick={handleReports}>View Dashboards</button>
              <button className="resto-secondary-btn" onClick={handleReports}>Customize Views</button>
            </div>
          </div>

          <div className="resto-section-image resto-fade-in-right">
            <img src="/images/reo1.png" className="resto-real-image" alt="Real-Time Dashboards" />
          </div>
        </section>

        {/* 2. Custom Reporting */}
        <section
          ref={(el) => (sectionsRef.current["custom-reporting"] = el)}
          data-section="custom-reporting"
          className={`resto-section resto-reverse ${visibleSections.has("custom-reporting") ? "resto-visible" : ""}`}
        >
          <div className="resto-section-content resto-fade-in-right">
            <div className="resto-section-label">Reporting</div>
            <h2 className="resto-section-title">
              {sectionContent["custom-reporting"].title}
            </h2>
            <p className="resto-section-description">
              {sectionContent["custom-reporting"].description}
            </p>

            <div className="resto-buttons-group">
              <button className="resto-primary-btn" onClick={handleReports}>Create Report</button>
              <button className="resto-secondary-btn" onClick={handleReports}>Browse Templates</button>
            </div>
          </div>

          <div className="resto-section-image resto-fade-in-left">
            <img src="/images/reo2.png" className="resto-real-image" alt="Custom Reporting" />
          </div>
        </section>

        <CTA/>

        {/* 3. Predictive Analytics */}
        <section
          ref={(el) => (sectionsRef.current["predictive-analytics"] = el)}
          data-section="predictive-analytics"
          className={`resto-section ${visibleSections.has("predictive-analytics") ? "resto-visible" : ""}`}
        >
          <div className="resto-section-content resto-fade-in-left">
            <div className="resto-section-label">Analytics</div>
            <h2 className="resto-section-title">
              {sectionContent["predictive-analytics"].title}
            </h2>
            <p className="resto-section-description">
              {sectionContent["predictive-analytics"].description}
            </p>

            <div className="resto-buttons-group">
              <button className="resto-primary-btn" onClick={handleReports}>Run Analysis</button>
              <button className="resto-secondary-btn" onClick={handleReports}>View Predictions</button>
            </div>
          </div>

          <div className="resto-section-image resto-fade-in-right">
            <img src="/images/reo3.png" className="resto-real-image" alt="Predictive Analytics" />
          </div>
        </section>

        {/* 4. Compliance Reporting */}
        <section
          ref={(el) => (sectionsRef.current["compliance-reporting"] = el)}
          data-section="compliance-reporting"
          className={`resto-section resto-reverse ${visibleSections.has("compliance-reporting") ? "resto-visible" : ""}`}
        >
          <div className="resto-section-content resto-fade-in-right">
            <div className="resto-section-label">Compliance</div>
            <h2 className="resto-section-title">
              {sectionContent["compliance-reporting"].title}
            </h2>
            <p className="resto-section-description">
              {sectionContent["compliance-reporting"].description}
            </p>

            <div className="resto-buttons-group">
              <button className="resto-primary-btn" onClick={handleReports}>Generate Reports</button>
              <button className="resto-secondary-btn" onClick={handleReports}>Track Compliance</button>
            </div>
          </div>

          <div className="resto-section-image resto-fade-in-left">
            <img src="/images/reo4.png" className="resto-real-image" alt="Compliance Reporting" />
          </div>
        </section>

        {/* 5. Data Visualization */}
        <section
          ref={(el) => (sectionsRef.current["data-visualization"] = el)}
          data-section="data-visualization"
          className={`resto-section ${visibleSections.has("data-visualization") ? "resto-visible" : ""}`}
        >
          <div className="resto-section-content resto-fade-in-left">
            <div className="resto-section-label">Visualization</div>
            <h2 className="resto-section-title">
              {sectionContent["data-visualization"].title}
            </h2>
            <p className="resto-section-description">
              {sectionContent["data-visualization"].description}
            </p>

            <div className="resto-buttons-group">
              <button className="resto-primary-btn" onClick={handleReports}>Create Visualizations</button>
              <button className="resto-secondary-btn" onClick={handleReports}>Share Insights</button>
            </div>
          </div>

          <div className="resto-section-image resto-fade-in-right">
            <img src="/images/reo5.png" className="resto-real-image" alt="Data Visualization" />
          </div>
        </section>

      </div>
    </div>
  );
};

export default ReportsInsightsModule;